// Ionic & Angular Imports
import {Component} from '@angular/core';
import {FormBuilder, Validators, FormGroup} from '@angular/forms';
import {Events, AlertController, PopoverController, Popover, ViewController, 
        NavParams, LoadingController, Loading} from 'ionic-angular';

// Service Imports
import {rsReportSvc} from '../../../../../services/report.service';
import {rsActivitySvc, IActivity} from '../../../../../services/activity.service';

// Component Imports
import {BasePage} from '../../../../base.component';
import {ActivityPopover} from '../activity-popover/activity-popover.component';

@Component({
    templateUrl: 'activity-modal.html',
    providers: [
        rsActivitySvc
    ]
})
export class ActivityModal extends BasePage
{
    // Class member variables
    private modalForm       : FormGroup;
    private editMode        : boolean;
    private initActivity    : IActivity;

    constructor( 
        protected alertCtrl : AlertController,
        private navParams : NavParams, 
        private reportSvc: rsReportSvc, 
        private activitySvc: rsActivitySvc,
        private loadingCtrl : LoadingController,
        private viewCtrl : ViewController,
        private formBuilder : FormBuilder,
        private popupCtrl : PopoverController,
        private eventSystem : Events
    ){
        // Construct base class
        super( alertCtrl );

        // Retrieve modal data from NavParams           
        this.editMode = this.navParams.get("editmode");
        if( this.editMode ) { 
            this.initActivity = <IActivity>this.navParams.get("activity");
        } else {
            // Initialize our activity object
            this.initActivity = <IActivity>{
                id: 0,
                title: "",
                details: "",
                complete: false
            };
        }

        // Build our form model structure
        this.buildFormModel();
    }

    // Class method for builidng our form model structure 
    private buildFormModel() {
        this.modalForm = this.formBuilder.group({
            title:      [this.initActivity.title, Validators.required],
            details:    [this.initActivity.details ],
            complete:   [this.initActivity.complete ]
        });
    }

    // Class helper method for setting a field in the form to a specified value
    private setFormField( fieldName: string, fieldValue: any ) {
        this.modalForm.controls[fieldName].setValue( fieldValue );
        this.modalForm.controls[fieldName].markAsDirty();
    }

    // Class helper method to allow the check box to toggle when the label is pressed
    public toggleComplete() {
        // Set hte form field to the opposite of the current value
        this.setFormField( 'complete', <boolean>!this.modalForm.controls['complete'].value)
    }    

    public selectTitlePreset(event) {
        // Create our popover instance
        let popover : Popover = this.popupCtrl.create( ActivityPopover, {}, {
            cssClass: "customPopover"
        } );
        
        // Handle popover callback
        popover.onWillDismiss( (data) => {
            // If there was a selection made
            if( data !== null && data.selected !== undefined ) {
                this.setFormField( 'title', data.selected );
            }
        });    

        // Present our popover component 
        popover.present({
            ev: event
        });    
    }

    // Class method to close the modal view 
    public modalClose() {
        this.viewCtrl.dismiss();
    }

    public modalReset() {
        this.modalForm.reset( {
            title:      this.initActivity.title,
            details:    this.initActivity.details,
            complete:   this.initActivity.complete            
        });
    }

    public modalSubmit( formData : any, isValid : boolean ) {
        // Display a loader while the modal submits our changes
        let loader : Loading = this.loadingCtrl.create({
            content: this.editMode ? 'Updating...' : 'Adding...',
            dismissOnPageChange: true
        });
        loader.present().then( () => {
            // Determine what the submit button shoudl be doing based on the validity
            // of the form and the mode of the modal.
            if( isValid === true && !this.editMode) {
                // The submit button should save a new activity
                this.activitySvc.addActivity( formData.title, formData.details, formData.complete, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('activity:added', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error adding this activity to the report.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                });
            } else if( isValid === true && this.editMode ) {
                // The submit button should save changes to an existing activity
                this.activitySvc.updateActivity( this.initActivity.id, formData.title, formData.details, formData.complete, this.reportSvc.getActiveReportId() )
                .subscribe( result => {
                    this.eventSystem.publish('activity:updated', result );
                }, err => {
                    // Operation failed; dismiss loader and display an error message
                    loader.dismiss().then( () => {
                        this.displayError("There was an error updating this activity.  Please try again later." );
                    });
                }, () => {
                    // Operation successful; close modal
                    this.modalClose();
                }); 
            }
        });
    }

    public modalDelete() {
        // Verify that we are in the correct editMode and that our id is valid
        if( this.editMode && this.initActivity.id > 0 )
        {
            // Activity id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Activity', 'Are you sure you want to delete "' + this.initActivity.title + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.activitySvc.deleteActivity( this.initActivity.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('activity:deleted', this.initActivity );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this activity.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        this.viewCtrl.dismiss().then( () => {
                            loader.dismiss();
                        });
                    });
                });
            });
        } else {
            // The activity id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }
}



// WEBPACK FOOTER //
// ./src/pages/report/tab-main/activity/activity-modal/activity-modal.component.ts